import {
  DefaultError,
  QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

export type UseApiMutationProps<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
  SetQueryDataData = unknown,
> = Parameters<typeof useMutation<TData, TError, TVariables, TContext>>[0] & {
  invalidateKeyOnSuccess?: QueryKey
  setQueryDataArgsOnSuccess?: [
    QueryKey,
    (prevData: SetQueryDataData) => SetQueryDataData,
  ]
}

export const useApiMutation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
  SetQueryDataData = unknown,
>({
  mutationFn,
  onSuccess,
  invalidateKeyOnSuccess,
  setQueryDataArgsOnSuccess,
  ...rest
}: UseApiMutationProps<
  TData,
  TError,
  TVariables,
  TContext,
  SetQueryDataData
>) => {
  const queryClient = useQueryClient()

  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn,
    onSuccess: (...args) => {
      if (invalidateKeyOnSuccess) {
        const keys = Array.isArray(invalidateKeyOnSuccess[0])
          ? (invalidateKeyOnSuccess as unknown[][])
          : [invalidateKeyOnSuccess]

        keys.forEach(key => {
          queryClient.invalidateQueries({ queryKey: key })
        })
      }
      if (setQueryDataArgsOnSuccess) {
        queryClient.setQueryData(...setQueryDataArgsOnSuccess)
      }
      onSuccess?.(...args)
    },
    ...rest,
  })
}
